import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useState, useEffect, useCallback } from 'react';
import { Edit, Plus, Trash2, Loader } from 'lucide-react';
import useSWR from 'swr';

import NewAccountService from '../../services/admin-account-service';
import NewRoleService from '../../services/role-service';
import { useAuth } from '../../contexts/AuthContext';

import { STATUS_MESSAGES } from '../../utils/api-handlers/http-status-code';
import { showToast } from '../../components/ToastConfig';

// Fetch roles data
const rolesFetcher = async () => {
  try {
    const res = await NewRoleService.search();
    return [{ name: '', displayName: '全部角色' }, ...res.data?.data];
  } catch (error) {
    console.error('Error fetching roles:', error);
    return [{ name: '', displayName: '全部角色' }];
  }
};

const STATUS_OPTIONS = [
  { value: '', label: '全部狀態' },
  { value: 'active', label: '啟用' },
  { value: 'inactive', label: '停用' },
];

const SORT_OPTIONS = [
  { value: 'asc', label: '正序' },
  { value: 'desc', label: '倒序' },
];

// SWR fetcher function
const fetcher = async ([url, keyword, role, status, sort, page]) => {
  try {
    // Transform URL parameters to API parameters
    const apiParams = {
      search: keyword,
      role,
      isEnabled: status === 'inactive' ? false : status === 'active' ? true : null,
      sortDirection: sort === 'asc' ? 'Ascending' : 'Descending',
      page
    };
    const res = await NewAccountService.search(apiParams, page);
    return res.data;
  } catch (error) {
    if (error.response?.status === 400 && ["1002", "8000"].includes(error.response?.data?.errorCode)) {
      return { data: [], pagination: { totalCount: 0 } };
    }
    throw error;
  }
};

function AccountList() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchInput, setSearchInput] = useState(searchParams.get('keyword') || '');
  const [pageInput, setPageInput] = useState(searchParams.get('page') || '1');
  const [isComposing, setIsComposing] = useState(false);
  const Navigate = useNavigate();
  const { logout } = useAuth();

  // Get search parameters from URL
  const keyword = searchParams.get('keyword') || '';
  const role = searchParams.get('role') || '';
  const status = searchParams.get('status') || '';
  const sort = searchParams.get('sort') || 'asc';
  const page = parseInt(searchParams.get('page') || '1');
  const pageSize = 10;

  // Fetch roles data
  const { data: rolesData } = useSWR('/api/Role/SearchAdmin', rolesFetcher, {
    revalidateOnFocus: false
  });
  const roleOptions = rolesData || [{ name: '', displayName: '全部角色' }];

  // Use SWR to fetch data
  const { data, error, isLoading, mutate } = useSWR(
    ['/api/AdminAccountManagement/Search', keyword, role, status, sort, page],
    fetcher,
    {
      onError: (err) => {
        if (error?.response?.status === 401 || error?.response?.status === 403) {
          logout();
        } else if (err.response?.status !== 400) {
          const errorMessage = STATUS_MESSAGES[err.response?.status] || STATUS_MESSAGES["default"];
          showToast.error(errorMessage);
        }
      },
      revalidateOnFocus: false,
    }
  );

  const [accountsWithStatus, setAccountsWithStatus] = useState([]);
  const accounts = data?.data || [];
  const totalItems = data?.pagination?.totalCount || 0;
  const totalPages = Math.ceil(totalItems / pageSize);

  useEffect(() => {
    if (accounts.length > 0) {
      setAccountsWithStatus(accounts.map(account => ({
        ...account,
        isUpdating: false
      })));
    }
  }, [accounts]);

  const handleToggleStatus = async (e, accountId, newStatus) => {
    e.preventDefault();
    e.stopPropagation();

    // Update local state to show loading
    setAccountsWithStatus(prev => prev.map(account => 
      account.id === accountId 
        ? { ...account, isUpdating: true }
        : account
    ));

    const targetAccount = accountsWithStatus.find(account => account.id === accountId);
    
    if (!targetAccount) {
      console.error('Target account not found');
      showToast.error('更新狀態失敗，請稍後再試');
      return;
    }

    try {
      await NewAccountService.update(accountId, {...targetAccount, isEnabled: newStatus});
      mutate(); // Refresh the data
      showToast.success(newStatus ? '帳號已啟用' : '帳號已停用');
    } catch (error) {
      console.error('Error updating account status:', error);
      if (error?.response?.status === 401 || error?.response?.status === 403) {
        logout();
      } else {
        showToast.error('更新狀態失敗，請稍後再試');
      }

      // Revert local state on error
      setAccountsWithStatus(prev => prev.map(account => 
        account.id === accountId 
          ? { ...account, isUpdating: false }
          : account
      ));
    }
  };

  // Handle search params update
  const updateSearchParams = useCallback((updates) => {
    const newParams = new URLSearchParams(searchParams);
    Object.entries(updates).forEach(([key, value]) => {
      if (value) {
        newParams.set(key, value);
      } else {
        newParams.delete(key);
      }
    });
    setSearchParams(newParams);
    if (updates.page) {
      setPageInput(updates.page);
    }
  }, [searchParams, setSearchParams]);

  // Search debounce
  useEffect(() => {
    if (isComposing) return;
    if (searchInput === keyword) return;

    const timer = setTimeout(() => {
      updateSearchParams({ keyword: searchInput, page: '1' });
    }, 500);

    return () => clearTimeout(timer);
  }, [searchInput, isComposing, updateSearchParams, keyword]);

  // const handleDelete = async (e, accountId) => {
  //   e.preventDefault();
  //   e.stopPropagation();

  //   if (!window.confirm('確定要刪除此帳號嗎？此操作無法復原。')) {
  //     return;
  //   }

  //   try {
  //     await NewAccountService.delete(accountId);
  //     mutate();
  //   } catch (error) {
  //     console.error('Error removing account:', error);
  //     showToast.error('刪除失敗，請稍後再試');
  //   }
  // };

  return (
    <div className={`p-6 pt-0 ${totalPages > 1 ? "h-[78vh]" : ""} overflow-auto`}>
      <div className="container mx-auto">
        <div className="sticky top-0 pt-6 pb-4 bg-white z-10">
          <h1 className="text-2xl font-bold">帳號管理</h1>
          
          {/* Search and filter controls */}
          <div className="space-y-4">
            <div className="flex justify-between mt-4">
              <div className="flex items-center">
                <Link className='flex items-center gap-2' to="/admin/new">
                    <Plus size={28} className='border border-gray-600 rounded-full p-1' />
                    <span>新增</span>
                </Link>
              </div>
              <div className="flex gap-4">
                <input
                  type="text"
                  placeholder="搜尋帳號..."
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  onCompositionStart={() => setIsComposing(true)}
                  onCompositionEnd={() => setIsComposing(false)}
                  className="px-3 py-2 border rounded"
                />
                <select
                  value={role}
                  onChange={(e) => updateSearchParams({ role: e.target.value, page: '1' })}
                  className="px-3 py-2 border rounded"
                >
                  {roleOptions.map((option, index) => (
                    <option key={option.name || index} value={option.name}>{option.displayName}</option>
                  ))}
                </select>
                <select
                  value={status}
                  onChange={(e) => updateSearchParams({ status: e.target.value, page: '1' })}
                  className="px-3 py-2 border rounded"
                >
                  {STATUS_OPTIONS.map(option => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
                <select
                  value={sort}
                  onChange={(e) => updateSearchParams({ sort: e.target.value, page: '1' })}
                  className="px-3 py-2 border rounded"
                >
                  {SORT_OPTIONS.map(option => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Loading state */}
        {isLoading && (
          <div className="text-center py-8 text-gray-500 flex items-center justify-center">
            <Loader className="mr-1 animate-spin w-5 h-5" />
            載入中...
          </div>
        )}

        {/* Error state */}
        {error && !isLoading && (
          <div className="text-center py-8 text-red-500">
            載入資料時發生錯誤，請稍後再試
          </div>
        )}

        {/* Empty state */}
        {!isLoading && !error && accounts.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            {keyword || role || status || sort ? '沒有符合搜尋條件的帳號' : '目前沒有任何帳號'}
          </div>
        )}

        {/* Account list */}
        {!isLoading && !error && accounts.length > 0 && (
          <div className="space-y-2">
            {/* Table header */}
            <div className="flex items-center p-4 bg-gray-50 rounded-t border-b font-medium justify-between">
              <div className="flex-1 flex">
                <div className="w-[5%]">ID</div>
                <div className="w-[20%]">姓名</div>
                <div className="w-[15%]">角色</div>
                <div className="w-[30%]">信箱</div>
                <div className="w-[20%]">職稱</div>
                <div className="w-[10%] text-center">啟用</div>
              </div>
              {/* <div className="w-[15%] flex justify-center">
                <div className="w-1/2 text-center">啟用</div>
                <div className="w-1/2 text-center">編輯</div>
              </div> */}
            </div>

            {/* Table body */}
            {accounts.map(account => (
              <Link key={account.id} to={`/admin/${account.id}`} className="flex items-center p-4 border rounded hover:bg-gray-50 justify-between">
                <div className="flex-1 flex">
                  <div className="w-[5%] overflow-auto">{account.id}</div>
                  <div className="w-[20%] overflow-auto">{account.name}</div>
                  <div className="w-[15%] overflow-auto">{account.role}</div>
                  <div className="w-[30%] overflow-auto">{account.email}</div>
                  <div className="w-[20%] overflow-auto">{account.jobTitle}</div>
                  <div className="w-[10%] flex justify-center">
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleToggleStatus(e, account.id, account.isEnabled !== true);
                      }}
                      disabled={account.isUpdating}
                      className={`relative inline-flex h-7 w-14 items-center rounded-full transition-colors duration-300 
                        ${account.isEnabled === true ? 'bg-green-600 bg-opacity-20' : 'bg-gray-200'}
                        ${account.isUpdating ? 'cursor-not-allowed' : ''}
                      `}
                    >
                      <span
                        className={`inline-block h-5 w-5 transform rounded-full transition-transform duration-300 ${
                          account.isEnabled === true ? 'translate-x-8 bg-green-600' : 'translate-x-1 bg-gray-400'
                        }`}
                      />
                      <span 
                        className={`absolute text-md ${
                          account.isEnabled === true
                            ? 'left-2 text-green-600'
                            : 'right-2 text-gray-500'
                        }`}
                      >
                        {account.isEnabled === true ? '是' : '否'}
                      </span>
                    </button>
                </div>
                </div>
                {/* <div className="w-[15%] flex"> */}
                  
                  {/* <div className="w-1/2 text-center">
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleToggleStatus(e, account.id, account.isEnabled !== true);
                      }}
                      disabled={account.isUpdating}
                      className={`relative inline-flex h-7 w-14 items-center rounded-full transition-colors duration-300 
                        ${account.isEnabled === true ? 'bg-green-600 bg-opacity-20' : 'bg-gray-200'}
                        ${account.isUpdating ? 'cursor-not-allowed' : ''}
                      `}
                    >
                      <span
                        className={`inline-block h-5 w-5 transform rounded-full transition-transform duration-300 ${
                          account.isEnabled === true ? 'translate-x-8 bg-green-600' : 'translate-x-1 bg-gray-400'
                        }`}
                      />
                      <span 
                        className={`absolute text-md ${
                          account.isEnabled === true
                            ? 'left-2 text-green-600'
                            : 'right-2 text-gray-500'
                        }`}
                      >
                        {account.isEnabled === true ? '是' : '否'}
                      </span>
                    </button>
                  </div> */}
                  {/* <div className="w-1/2 flex justify-center gap-2">
                    <Link
                      to={`/account/${account.id}/edit`}
                      className="text-blue-600 hover:text-blue-800 flex items-center"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Edit size={22} />
                    </Link>
                    <button
                      onClick={(e) => handleDelete(e, account.id)}
                      className="text-red-600 hover:text-red-800 flex items-center"
                    >
                      <Trash2 size={22} />
                    </button>
                  </div> */}
                {/* </div> */}
              </Link>
            ))}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex flex-col md:flex-row items-center justify-center gap-4 mt-10 fixed bottom-[4%] w-[calc(100%-15rem)]">
            <div className="flex items-center">
              <span className="text-gray-700">第</span>
              <input
                type="text"
                className="w-16 mx-2 px-2 py-1 border rounded text-center"
                value={pageInput}
                onChange={(e) => {
                  const value = e.target.value.replace(/[^0-9]/g, '');
                  setPageInput(value);
                }}
                onBlur={() => {
                  const pageNum = parseInt(pageInput);
                  if (isNaN(pageNum) || pageNum < 1 || pageNum > totalPages) {
                    setPageInput(page.toString());
                  }
                }}
              />
              <span className="text-gray-700">頁</span>
            </div>
            
            {/* Pagination buttons */}
            <div className="flex flex-wrap justify-center gap-2 w-full md:w-auto px-4">
              <button
                onClick={() => updateSearchParams({ page: String(page - 1) })}
                disabled={page === 1}
                className={`px-3 py-1 border rounded ${
                  page === 1 ? 'text-gray-400' : 'hover:bg-gray-100'
                }`}
              >
                上一頁
              </button>
              
              {/* First page */}
              <button
                onClick={() => updateSearchParams({ page: '1' })}
                className={`px-3 py-1 border rounded ${
                  1 === page ? 'bg-blue-500 text-white' : 'hover:bg-gray-100'
                }`}
              >
                1
              </button>

              {/* Pagination dots and numbers */}
              {page > 6 && <span className="px-3 py-1">...</span>}

              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter(pageNum => {
                  const showRange = 4;
                  return pageNum > page - showRange && pageNum < page + showRange;
                })
                .filter(pageNum => pageNum !== 1 && pageNum !== totalPages)
                .map(pageNum => (
                  <button
                    key={pageNum}
                    onClick={() => updateSearchParams({ page: String(pageNum) })}
                    className={`px-3 py-1 border rounded ${
                      pageNum === page ? 'bg-blue-500 text-white' : 'hover:bg-gray-100'
                    }`}
                  >
                    {pageNum}
                  </button>
                ))}

              {page < totalPages - 5 && <span className="px-3 py-1">...</span>}

              {/* Last page */}
              {totalPages > 1 && (
                <button
                  onClick={() => updateSearchParams({ page: String(totalPages) })}
                  className={`px-3 py-1 border rounded ${
                    totalPages === page ? 'bg-blue-500 text-white' : 'hover:bg-gray-100'
                  }`}
                >
                  {totalPages}
                </button>
              )}

              <button
                onClick={() => updateSearchParams({ page: String(page + 1) })}
                disabled={page === totalPages}
                className={`px-3 py-1 border rounded ${
                  page === totalPages ? 'text-gray-400' : 'hover:bg-gray-100'
                }`}
              >
                下一頁
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default AccountList;